import CONFIG from '../config/config';
import { instanceWithV2 as APIClient } from './SimpleAPIClient';

export interface BookingSubmissionResponseDto {
  bookingId: number;
  reservationReference: string;
  status: string;
  reason?: string;
}

export type PendingBookStatus = "PENDING" | "APPROVED" | "REJECTED" | "CANCELED_BY_BRIDE"

export interface PendingBookingDto {
  reservationReference?: string;
  bookingPlanId: number;
  appointmentDate: string; // Format: 'YYYY-MM-DD'
  appointmentTime: string; // Format: 'HH:mm:ss'
  customerData: { [key: string]: string };
  status?: PendingBookStatus;
}

export interface BookingOfferDto {
  storeName: string;
  logoUrl: string;
  plans: BookingPlanDto[];
  contactInfo: ContactInfoDto;
  currency?: string;
  language?: string;
}

// TODO: endpoint needs to be updated to return this DTO
export interface ChoosableBookingPlanDto {
  id: number;
  name: string;
  duration: number; // in minutes
  fee: number;
  feeCurrency: string;
  availableDays: number[]; // e.g., [1,2,3] for Monday, Tuesday, Wednesday
  requiresFee: boolean;
  customerInfo: string;
}

interface ContactInfoDto {
  email: string;
  phoneNumber: string;
  address: string;
  instagram?: string;
}

export interface BookingPlanDto {
  id: number;
  name: string;
  internalType: string;
  customerInfo: string;
  automaticallyAccept: boolean;
  requiresFee: boolean;
  duration: string;
  fee: number;
  feeCurrency: string;
  availableDays: Set<number>;

  requestEmail: boolean;
  requestPhone: boolean;
  requestName: boolean;
  requestBudget: boolean;
  requestSize: boolean;
  requestWeddingDate: boolean;
  requestNumberOfPeople: boolean;
  allowSpecialComments: boolean;
  requestWhereHeardFrom: boolean;
  budgetRanges: {max: string, min: string}[];
  brideCanCancel?: boolean;
  requiresRefundableFee: boolean;
  refundableFee: number;
  refundableFeeCurrency: string;
  useAdditionalRestrictions: boolean;
  startDate?: string;
  endDate?: string;
  minimumAdvanceBookingDays?: number;
  maximumAdvanceBookingDays?: number;
  useOnlinePayments: boolean;
  
  feeIsDeposit?: boolean;
  totalPrice?: number;
  totalPriceCurrency?: string;

  exclusive: boolean;
}

export interface StoreDetailsDto {
  name: string;
  address: string;
  contactPhone: string;
  website?: string;
  logoUrl?: string;
  contactEmail?: string;
  instagram?: string;
}

export interface MeetingChangesDto {
  updatedStartDate?: string;
  updatedStartTime?: string;
  updatedEndDate?: string;
  updatedEndTime?: string;
}

export type PaymentStatusType = "SUCCEEDED" | "FAILED" | "PENDING" | "NOT_REQUIRED" | "REFUNDED" | "DISPUTED" | "CANCELED";

export interface PaymentStatusDto {
  status: PaymentStatusType;
  currency?: string;
  amount?: string;
  reason?: string;
  isRefundable: boolean;
  refundedAmount?: string;
}

export interface BookingDetailsDto {
  pendingBooking: PendingBookingDto;
  storeDetails: StoreDetailsDto;
  bookingPlan: BookingPlanDto;
  canCancel: boolean;
  meetingChanges: MeetingChangesDto;
  paymentStatus?: PaymentStatusDto;
  success: boolean;
  error?: string;
  reason?: string;
}

const BookingAPI = {
  get: (id: string) => APIClient(CONFIG.API_ENDPOINT).get<BookingOfferDto>(`v1/public/bookings/${id}/offers`),
  availability: (id: number, year: number, month: number) => APIClient(CONFIG.API_ENDPOINT).get<any>(`v1/public/bookings/plans/${id}/availability?year=${year}&month=${month}`),
  submit: (id: string, booking: PendingBookingDto, locale: string) => APIClient(CONFIG.API_ENDPOINT).post<BookingDetailsDto>(`v1/public/bookings/${id}/submit?locale=${locale}`, booking),
  getReservationDetails: (bookingWidgetId: string, reservationId: string) =>
    APIClient(CONFIG.API_ENDPOINT).get<BookingDetailsDto>(
      `v1/public/bookings/${bookingWidgetId}/reservations/${reservationId}`
    ),
  cancelReservation: (bookingWidgetId: string, reservationId: string) =>
    APIClient(CONFIG.API_ENDPOINT).delete<BookingSubmissionResponseDto>(
      `v1/public/bookings/${bookingWidgetId}/reservations/${reservationId}`
    ),
  createPaymentSession: async (bookingWidgetId: string, bookingData: PendingBookingDto, locale: string): Promise<any> => {
    try {
      const response = await APIClient(CONFIG.API_ENDPOINT).post<any>(`v1/public/bookings/${bookingWidgetId}/payment?locale=${locale}`, bookingData);
      return response.data;
    } catch (error) {
      console.error('Error creating payment session:', error);
      throw error;
    }
  },
  createPaymentSessionForExistingBooking: (bookingWidgetId: string, reservationId: string, locale: string) => 
    APIClient(CONFIG.API_ENDPOINT).post<any>(
      `v1/public/bookings/${bookingWidgetId}/reservations/${reservationId}/payment?locale=${locale}`
    ),
};

export default BookingAPI;
